import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Download } from 'lucide-react'
import type {
  QuickBooksArCollectabilityAnalysis,
  QuickBooksArCollectabilityCustomer,
  QuickBooksArCollectabilityInvoice,
  QuickBooksArCollectabilityThreshold,
  QuickBooksArCustomerConcern,
  QuickBooksArIncomeAccountAllocation,
  QuickBooksArInvoiceDrilldown,
} from '../../../data/ServerApiClient'
import { Sparkline } from './charts'
import type { CfoArExcelColumn } from './cfoArExcelExport'
import {
  AR_COLLECTABILITY_CUSTOMER_EXCEL_COLUMNS,
  AR_COLLECTABILITY_INVOICE_EXCEL_COLUMNS,
  AR_CUSTOMER_EXCEL_COLUMNS,
  AR_INCOME_ACCOUNT_EXCEL_COLUMNS,
  AR_INVOICE_EXCEL_COLUMNS,
  AR_CHANGE_DRIVER_EXCEL_COLUMNS,
  type ArIncomeAccountAllocationSortColumn,
  type ArIncomeAccountColumnId,
  type ArIncomeAccountOption,
  type ArIncomeAccountReportView,
  type ArCollectabilityCustomerSortColumn,
  type ArCollectabilityInvoiceSortColumn,
  type ArCollectabilityView,
  type ArCustomerChangeDriver,
  type ArCustomerSortColumn,
  type ArInvoiceSortColumn,
  type ArSortDirection,
  type ArSortState,
  arCustomerKey,
  arIncomeAccountDisplayName,
  formatArCurrency,
  formatArDayNumber,
  formatArDays,
  formatArPercent,
  formatArSignedCurrency,
  quickBooksArInvoiceOver90Amount,
  sortArIncomeAccountAllocations,
  sortArCollectabilityCustomers,
  sortArCollectabilityInvoices,
  sortArCustomers,
  sortArInvoices,
} from './cfoArView'

// CfoArSections — the progressive-depth A/R bodies (change drivers, customers,
// invoices, collectability & reserve) carried forward from the legacy A/R Review
// and Collectability panels, rebuilt as native CFO tables. Each is mounted only
// when its CfoDepthSection is opened. They are presentational: the page owns the
// analysis, exclusion state, and the Excel export wiring (passed as `onExport`).

export type CfoArExportTable = (args: {
  title: string
  sheetName: string
  fileSegment: string
  rows: any[]
  columns: CfoArExcelColumn<any>[]
  meta?: string | null
}) => void

// ---- shared bits -----------------------------------------------------------

export function CfoArDownloadButton({
  label,
  disabled,
  available,
  onClick,
}: {
  label: string
  disabled?: boolean
  available: boolean
  onClick: () => void
}) {
  const off = disabled || !available
  return (
    <button
      type="button"
      className="cfo-ar-icon-btn"
      onClick={onClick}
      disabled={off}
      aria-label={`Download ${label} to Excel`}
      title={!available ? 'Excel export is unavailable in this session' : `Download ${label} to Excel`}
    >
      <Download size={14} strokeWidth={2.2} aria-hidden="true" />
    </button>
  )
}

function CfoArSectionBar({ title, meta, action }: { title: ReactNode; meta?: ReactNode; action?: ReactNode }) {
  return (
    <div className="cfo-ar-section-bar">
      <div className="cfo-ar-section-bar-title">{title}</div>
      <div className="cfo-ar-section-bar-end">
        {meta ? <span className="cfo-ar-section-bar-meta">{meta}</span> : null}
        {action}
      </div>
    </div>
  )
}

const sumBy = <T,>(rows: T[], value: (row: T) => number | null | undefined): number =>
  rows.reduce((sum, row) => {
    const next = Number(value(row) || 0)
    return Number.isFinite(next) ? sum + next : sum
  }, 0)

function CfoArTableTotals({ items }: { items: Array<{ label: string; value: ReactNode; tone?: 'good' | 'risk' | 'muted' }> }) {
  return (
    <dl className="cfo-ar-table-totals" aria-label="Table totals">
      {items.map((item) => (
        <div key={item.label} className={item.tone ? `is-${item.tone}` : undefined}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

const sortGlyph = (active: boolean, direction: ArSortDirection): string => (active ? (direction === 'asc' ? '▲' : '▼') : '')

function SortHeader<C extends string>({
  columnId,
  label,
  sort,
  onSort,
  align = 'right',
  defaultDirection,
}: {
  columnId: C
  label: string
  sort: ArSortState<C>
  onSort: (next: ArSortState<C>) => void
  align?: 'left' | 'right'
  defaultDirection?: ArSortDirection
}) {
  const active = sort.columnId === columnId
  const fallback: ArSortDirection = defaultDirection ?? (align === 'left' ? 'asc' : 'desc')
  return (
    <th
      style={{ textAlign: align }}
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="cfo-ar-sort-btn"
        style={{ justifyContent: align === 'left' ? 'flex-start' : 'flex-end' }}
        onClick={() =>
          onSort(active ? { columnId, direction: sort.direction === 'asc' ? 'desc' : 'asc' } : { columnId, direction: fallback })
        }
        title={`Sort by ${label}`}
      >
        <span>{label}</span>
        <span aria-hidden="true" className="cfo-ar-sort-glyph">{sortGlyph(active, sort.direction)}</span>
      </button>
    </th>
  )
}

// ---- declarative table columns ----------------------------------------------
//
// A single, declarative column model shared by every A/R table. Each column
// knows how to render its own head + cell; sortable columns declare a `sort`
// key and get a SortHeader, the rest render a plain <th>. Tables render their
// full declared column set — the per-table control is the income-account
// filter (which slices rows, not columns), passed in by the page as `filter`.

type ArDataColumn<Row, S extends string = string> = {
  id: string
  label: string
  sort?: S
  align?: 'left' | 'right'
  defaultDirection?: ArSortDirection
  headWidth?: number
  render: (row: Row) => ReactNode
  cellStyle?: (row: Row) => CSSProperties | undefined
}

/** Head cell for a data column: a SortHeader when sortable, else a plain <th>. */
function ArColumnHead<Row, S extends string>({
  column,
  sort,
  onSort,
}: {
  column: ArDataColumn<Row, S>
  sort?: ArSortState<S>
  onSort?: (next: ArSortState<S>) => void
}) {
  if (column.sort && sort && onSort) {
    return (
      <SortHeader
        columnId={column.sort}
        label={column.label}
        sort={sort}
        onSort={onSort}
        align={column.align || 'right'}
        defaultDirection={column.defaultDirection}
      />
    )
  }
  return <th style={{ textAlign: column.align || 'right', width: column.headWidth }}>{column.label}</th>
}

/** Body cells for one row across the visible data columns. */
function ArColumnCells<Row, S extends string>({
  columns,
  row,
}: {
  columns: ReadonlyArray<ArDataColumn<Row, S>>
  row: Row
}) {
  return (
    <>
      {columns.map((column) => (
        <td key={column.id} style={{ textAlign: column.align || 'right', ...(column.cellStyle?.(row) || {}) }}>
          {column.render(row)}
        </td>
      ))}
    </>
  )
}

// ---- Change drivers --------------------------------------------------------

const CHANGE_DRIVER_COLUMNS: ReadonlyArray<ArDataColumn<ArCustomerChangeDriver>> = [
  { id: 'customer', label: 'Customer', align: 'left', render: (row) => row.customer, cellStyle: () => ({ fontWeight: 600 }) },
  { id: 'previousEndingAr', label: 'Prior A/R', render: (row) => formatArCurrency(row.previousEndingAr) },
  { id: 'endingAr', label: 'Selected A/R', render: (row) => formatArCurrency(row.endingAr) },
  {
    id: 'endingDelta',
    label: 'Change',
    render: (row) => formatArSignedCurrency(row.endingDelta),
    cellStyle: (row) => ({ color: row.endingDelta >= 0 ? 'var(--neg, #b91c1c)' : 'var(--pos, #047857)', fontWeight: 700 }),
  },
  { id: 'currentDelta', label: 'Current', render: (row) => formatArSignedCurrency(row.currentDelta) },
  { id: 'under90Delta', label: '1-90', render: (row) => formatArSignedCurrency(row.under90Delta) },
  { id: 'over90Delta', label: '>90', render: (row) => formatArSignedCurrency(row.over90Delta) },
]

export function CfoArChangeDriversSection({
  drivers,
  rangeLabel,
  selectedCustomerKey,
  onSelectCustomer,
  onExcludeCustomer,
  exportAvailable,
  onExport,
  filter,
}: {
  drivers: ArCustomerChangeDriver[]
  rangeLabel: string
  selectedCustomerKey: string
  onSelectCustomer: (customerKey: string) => void
  onExcludeCustomer: (customer: QuickBooksArCustomerConcern) => void
  exportAvailable: boolean
  onExport: CfoArExportTable
  filter?: ReactNode
}) {
  const totals = useMemo(() => [
    { label: 'Prior A/R', value: formatArCurrency(sumBy(drivers, (driver) => driver.previousEndingAr)) },
    { label: 'Selected A/R', value: formatArCurrency(sumBy(drivers, (driver) => driver.endingAr)) },
    {
      label: 'Net change',
      value: formatArSignedCurrency(sumBy(drivers, (driver) => driver.endingDelta)),
      tone: sumBy(drivers, (driver) => driver.endingDelta) >= 0 ? 'risk' as const : 'good' as const,
    },
    { label: '90+ change', value: formatArSignedCurrency(sumBy(drivers, (driver) => driver.over90Delta)) },
  ], [drivers])

  if (!drivers.length && !filter) {
    return <div className="cfo-shell-notice">No customer-level A/R change of one currency unit or more between {rangeLabel}.</div>
  }
  return (
    <section className="cfo-ar-card">
      <CfoArSectionBar
        title="A/R change drivers"
        meta={rangeLabel}
        action={
          <CfoArDownloadButton
            label="A/R change drivers"
            available={exportAvailable}
            disabled={!drivers.length}
            onClick={() =>
              onExport({
                title: 'A/R change drivers',
                sheetName: 'Change Drivers',
                fileSegment: 'AR Change Drivers',
                rows: drivers,
                columns: AR_CHANGE_DRIVER_EXCEL_COLUMNS,
                meta: rangeLabel,
              })
            }
          />
        }
      />
      <CfoArTableTotals items={totals} />
      {filter}
      <div className="dtable-wrap cfo-ar-table-viewport">
        <table className="dtable">
          <thead>
            <tr>
              {CHANGE_DRIVER_COLUMNS.map((column) => <ArColumnHead key={column.id} column={column} />)}
              <th aria-label="Actions" style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {drivers.length ? drivers.map((driver) => {
              const driverKey = arCustomerKey(driver)
              const active = Boolean(selectedCustomerKey && driverKey === selectedCustomerKey)
              return (
                <tr
                  key={`ar-change-${driverKey || driver.customer}`}
                  onClick={() => onSelectCustomer(active ? '' : driverKey)}
                  className={active ? 'cfo-ar-row-active' : undefined}
                  style={{ cursor: 'pointer' }}
                >
                  <ArColumnCells columns={CHANGE_DRIVER_COLUMNS} row={driver} />
                  <td>
                    <button
                      type="button"
                      className="cfo-ar-exclude-btn"
                      onClick={(event) => {
                        event.stopPropagation()
                        onExcludeCustomer({
                          customerId: driver.customerId,
                          customer: driver.customer,
                          endingAr: driver.endingAr,
                          overdueAr: 0,
                          over60Ar: 0,
                          over90Ar: 0,
                          dso: null,
                          weightedAverageAge: null,
                          riskScore: Math.abs(driver.endingDelta),
                          openInvoiceCount: null,
                        })
                      }}
                      title={`Exclude ${driver.customer} from A/R analysis`}
                    >
                      Exclude
                    </button>
                  </td>
                </tr>
              )
            }) : (
              <tr>
                <td colSpan={CHANGE_DRIVER_COLUMNS.length + 1} style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  No customer-level A/R change of one currency unit or more between {rangeLabel} for this selection.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ---- Customers -------------------------------------------------------------

const CUSTOMER_COLUMNS: ReadonlyArray<ArDataColumn<QuickBooksArCustomerConcern, ArCustomerSortColumn>> = [
  { id: 'customer', label: 'Customer', sort: 'customer', align: 'left', render: (row) => row.customer, cellStyle: () => ({ fontWeight: 600 }) },
  { id: 'endingAr', label: 'Open', sort: 'endingAr', render: (row) => formatArCurrency(row.endingAr) },
  { id: 'overdueAr', label: 'Overdue', sort: 'overdueAr', render: (row) => formatArCurrency(row.overdueAr) },
  { id: 'over90Ar', label: '>90 Days', sort: 'over90Ar', render: (row) => formatArCurrency(row.over90Ar) },
  { id: 'dso', label: 'DSO', sort: 'dso', render: (row) => formatArDayNumber(row.dso) },
]

export function CfoArCustomersSection({
  customers,
  candidates,
  metaLabel,
  selectedCustomerKey,
  onSelectCustomer,
  onExcludeCustomer,
  exportAvailable,
  onExport,
  filter,
}: {
  customers: QuickBooksArCustomerConcern[]
  candidates: QuickBooksArCustomerConcern[]
  metaLabel: string
  selectedCustomerKey: string
  onSelectCustomer: (customerKey: string) => void
  onExcludeCustomer: (customer: QuickBooksArCustomerConcern) => void
  exportAvailable: boolean
  onExport: CfoArExportTable
  filter?: ReactNode
}) {
  const [sort, setSort] = useState<ArSortState<ArCustomerSortColumn>>({ columnId: 'over90Ar', direction: 'desc' })
  const [search, setSearch] = useState('')

  const visible = useMemo(
    () => sortArCustomers(customers.filter((customer) => Math.abs(Number(customer.endingAr || 0)) >= 1), sort),
    [customers, sort],
  )
  const searchMatches = useMemo(() => {
    const query = search.trim().toLowerCase()
    const pool = candidates.filter((customer) => Math.abs(Number(customer.endingAr || 0)) >= 0.005)
    if (!query) return pool.slice(0, 8)
    return pool.filter((customer) => (customer.customer || '').toLowerCase().includes(query)).slice(0, 8)
  }, [candidates, search])
  const totals = useMemo(() => [
    { label: 'Customers', value: visible.length.toLocaleString(), tone: 'muted' as const },
    { label: 'Open A/R', value: formatArCurrency(sumBy(visible, (customer) => customer.endingAr)) },
    { label: 'Overdue', value: formatArCurrency(sumBy(visible, (customer) => customer.overdueAr)) },
    { label: '>90 Days', value: formatArCurrency(sumBy(visible, (customer) => customer.over90Ar)), tone: 'risk' as const },
  ], [visible])

  return (
    <section className="cfo-ar-card">
      <CfoArSectionBar
        title="Customers"
        meta={`${visible.length} open balance${visible.length === 1 ? '' : 's'} · ${metaLabel}`}
        action={
          <CfoArDownloadButton
            label="A/R customers"
            available={exportAvailable}
            disabled={!visible.length}
            onClick={() =>
              onExport({
                title: 'A/R customers',
                sheetName: 'Customers',
                fileSegment: 'AR Customers',
                rows: visible,
                columns: AR_CUSTOMER_EXCEL_COLUMNS,
                meta: metaLabel,
              })
            }
          />
        }
      />
      <CfoArTableTotals items={totals} />
      <div className="cfo-ar-exclude-search">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Find a customer to exclude"
          aria-label="Find a customer to exclude from A/R analysis"
        />
        {searchMatches.length ? (
          <div className="cfo-ar-exclude-pills">
            {searchMatches.map((customer) => (
              <button
                type="button"
                key={`exclude-pill-${arCustomerKey(customer) || customer.customer}`}
                className="cfo-ar-exclude-pill"
                onClick={() => {
                  onExcludeCustomer(customer)
                  setSearch('')
                }}
                title={`Exclude ${customer.customer} from A/R analysis`}
              >
                {customer.customer} · {formatArCurrency(customer.endingAr)}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {filter}
      <div className="dtable-wrap cfo-ar-table-viewport">
        <table className="dtable">
          <thead>
            <tr>
              {CUSTOMER_COLUMNS.map((column) => <ArColumnHead key={column.id} column={column} sort={sort} onSort={setSort} />)}
              <th aria-label="Actions" style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {visible.length ? (
              visible.map((customer) => {
                const key = arCustomerKey(customer)
                const active = Boolean(selectedCustomerKey && key === selectedCustomerKey)
                return (
                  <tr
                    key={`ar-customer-${key || customer.customer}`}
                    onClick={() => onSelectCustomer(active ? '' : key)}
                    className={active ? 'cfo-ar-row-active' : undefined}
                    style={{ cursor: 'pointer' }}
                  >
                    <ArColumnCells columns={CUSTOMER_COLUMNS} row={customer} />
                    <td>
                      <button
                        type="button"
                        className="cfo-ar-exclude-btn"
                        onClick={(event) => {
                          event.stopPropagation()
                          onExcludeCustomer(customer)
                        }}
                        title={`Exclude ${customer.customer} from A/R analysis`}
                      >
                        Exclude
                      </button>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={CUSTOMER_COLUMNS.length + 1} style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  No customers with open balances of one currency unit or more for this selection.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ---- Invoices --------------------------------------------------------------

const INVOICE_COLUMNS: ReadonlyArray<ArDataColumn<QuickBooksArInvoiceDrilldown, ArInvoiceSortColumn>> = [
  { id: 'invoiceNumber', label: 'Invoice', sort: 'invoiceNumber', align: 'left', render: (row) => row.invoiceNumber || row.invoiceKey, cellStyle: () => ({ fontWeight: 600 }) },
  {
    id: 'customer',
    label: 'Customer',
    sort: 'customer',
    align: 'left',
    render: (row) => row.customer || '',
    cellStyle: () => ({ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
  },
  { id: 'invoiceDate', label: 'Invoice date', sort: 'invoiceDate', defaultDirection: 'asc', render: (row) => row.invoiceDate || '' },
  { id: 'dueDate', label: 'Due', sort: 'dueDate', defaultDirection: 'asc', render: (row) => row.dueDate || '' },
  { id: 'openAmount', label: 'Open', sort: 'openAmount', render: (row) => formatArCurrency(row.openAmount) },
  { id: 'over90Amount', label: '>90 Days', sort: 'over90Amount', render: (row) => formatArCurrency(quickBooksArInvoiceOver90Amount(row)) },
  { id: 'daysPastDue', label: 'Past due', sort: 'daysPastDue', render: (row) => (row.daysPastDue == null ? 'N/A' : formatArDays(row.daysPastDue)) },
]

export function CfoArInvoicesSection({
  invoices,
  headerLabel,
  metaLabel,
  exportAvailable,
  onExport,
  filter,
}: {
  invoices: QuickBooksArInvoiceDrilldown[]
  headerLabel: string
  metaLabel: string
  exportAvailable: boolean
  onExport: CfoArExportTable
  filter?: ReactNode
}) {
  const [sort, setSort] = useState<ArSortState<ArInvoiceSortColumn>>({ columnId: 'over90Amount', direction: 'desc' })
  const sorted = useMemo(() => sortArInvoices(invoices, sort), [invoices, sort])
  const totals = useMemo(() => [
    { label: 'Invoices', value: sorted.length.toLocaleString(), tone: 'muted' as const },
    { label: 'Open A/R', value: formatArCurrency(sumBy(sorted, (invoice) => invoice.openAmount)) },
    { label: '>90 Days', value: formatArCurrency(sumBy(sorted, quickBooksArInvoiceOver90Amount)), tone: 'risk' as const },
    { label: 'Past due', value: `${sorted.filter((invoice) => Number(invoice.daysPastDue || 0) > 0).length.toLocaleString()} invoices` },
  ], [sorted])
  return (
    <section className="cfo-ar-card">
      <CfoArSectionBar
        title={headerLabel}
        meta={`${sorted.length} invoice${sorted.length === 1 ? '' : 's'}`}
        action={
          <CfoArDownloadButton
            label="A/R invoices"
            available={exportAvailable}
            disabled={!sorted.length}
            onClick={() =>
              onExport({
                title: 'A/R invoices',
                sheetName: 'Invoices',
                fileSegment: 'AR Invoices',
                rows: sorted,
                columns: AR_INVOICE_EXCEL_COLUMNS,
                meta: metaLabel,
              })
            }
          />
        }
      />
      <CfoArTableTotals items={totals} />
      {filter}
      <div className="dtable-wrap cfo-ar-table-viewport">
        <table className="dtable">
          <thead>
            <tr>
              {INVOICE_COLUMNS.map((column) => <ArColumnHead key={column.id} column={column} sort={sort} onSort={setSort} />)}
            </tr>
          </thead>
          <tbody>
            {sorted.length ? (
              sorted.map((invoice) => (
                <tr key={`${invoice.periodStart || ''}-${invoice.invoiceKey}`}>
                  <ArColumnCells columns={INVOICE_COLUMNS} row={invoice} />
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={INVOICE_COLUMNS.length} style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  No open invoices for this selection.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ---- Customers & income accounts -----------------------------------------

const INCOME_ACCOUNT_COLUMNS: Array<{
  id: ArIncomeAccountColumnId
  label: string
  sort?: ArIncomeAccountAllocationSortColumn
  align?: 'left' | 'right'
  defaultVisible: boolean
  render: (row: QuickBooksArIncomeAccountAllocation) => ReactNode
}> = [
  { id: 'customer', label: 'Customer', sort: 'customer', align: 'left', defaultVisible: true, render: (row) => row.customer || '' },
  { id: 'invoiceNumber', label: 'Invoice', sort: 'invoiceNumber', align: 'left', defaultVisible: false, render: (row) => row.invoiceNumber || row.invoiceKey || '' },
  { id: 'invoiceDate', label: 'Invoice date', sort: 'invoiceDate', defaultVisible: false, render: (row) => row.invoiceDate || '' },
  { id: 'dueDate', label: 'Due', sort: 'dueDate', defaultVisible: false, render: (row) => row.dueDate || '' },
  { id: 'incomeAccountName', label: 'Income account', sort: 'incomeAccountName', align: 'left', defaultVisible: true, render: arIncomeAccountDisplayName },
  { id: 'serviceItemName', label: 'Service item', sort: 'serviceItemName', align: 'left', defaultVisible: false, render: (row) => row.serviceItemName || '' },
  { id: 'className', label: 'Class', sort: 'className', align: 'left', defaultVisible: false, render: (row) => row.className || '' },
  { id: 'agingBucket', label: 'Aging', defaultVisible: true, render: (row) => row.agingBucket || '' },
  { id: 'openAmount', label: 'Open', sort: 'openAmount', defaultVisible: true, render: (row) => formatArCurrency(row.openAmount) },
  { id: 'currentAmount', label: 'Current', sort: 'currentAmount', defaultVisible: true, render: (row) => formatArCurrency(row.currentAmount) },
  { id: 'oneToThirtyAmount', label: '1-30', sort: 'oneToThirtyAmount', defaultVisible: true, render: (row) => formatArCurrency(row.oneToThirtyAmount) },
  { id: 'thirtyOneToSixtyAmount', label: '31-60', sort: 'thirtyOneToSixtyAmount', defaultVisible: true, render: (row) => formatArCurrency(row.thirtyOneToSixtyAmount) },
  { id: 'sixtyOneToNinetyAmount', label: '61-90', sort: 'sixtyOneToNinetyAmount', defaultVisible: true, render: (row) => formatArCurrency(row.sixtyOneToNinetyAmount) },
  { id: 'overNinetyAmount', label: '90+', sort: 'overNinetyAmount', defaultVisible: true, render: (row) => formatArCurrency(row.overNinetyAmount) },
  { id: 'daysPastDue', label: 'Past due', sort: 'daysPastDue', defaultVisible: false, render: (row) => formatArDayNumber(row.daysPastDue) },
  { id: 'unclassifiedReason', label: 'Exception', defaultVisible: false, render: (row) => row.unclassifiedReason || '' },
]

const DEFAULT_INCOME_ACCOUNT_COLUMNS = INCOME_ACCOUNT_COLUMNS.filter((column) => column.defaultVisible)

export function CfoArIncomeAccountFilter({
  view,
  selectedAccountIds,
  onSelectedAccountIdsChange,
  compact = false,
}: {
  view: ArIncomeAccountReportView
  selectedAccountIds: string[]
  onSelectedAccountIdsChange: (ids: string[]) => void
  compact?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const [draftAccountIds, setDraftAccountIds] = useState<string[]>(selectedAccountIds)
  useEffect(() => {
    setDraftAccountIds(selectedAccountIds)
  }, [selectedAccountIds])
  const draftSet = useMemo(() => new Set(draftAccountIds), [draftAccountIds])
  const draftAllAccounts = draftAccountIds.length === 0
  const toggleAccount = (account: ArIncomeAccountOption) => {
    const next = new Set(draftSet)
    if (draftAllAccounts) {
      for (const option of view.accountOptions) next.add(option.id)
    }
    if (next.has(account.id)) next.delete(account.id)
    else next.add(account.id)
    setDraftAccountIds(next.size === view.accountOptions.length ? [] : [...next])
  }
  const useAllAccounts = () => setDraftAccountIds([])
  const cancelDraft = () => {
    setDraftAccountIds(selectedAccountIds)
    setExpanded(false)
  }
  const applyDraft = () => {
    onSelectedAccountIdsChange(draftAccountIds)
    setExpanded(false)
  }
  const optionCount = view.accountOptions.length

  if (!view.allRows.length) {
    return (
      <div className="cfo-ar-income-filter is-empty">
        <div>
          <strong>Income account filter</strong>
          <span>Waiting for rebuilt A/R allocation rows.</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`cfo-ar-income-filter${compact ? ' is-compact' : ''}${expanded ? ' is-expanded' : ''}`}>
      <div className="cfo-ar-income-filter-head">
        <div>
          <strong>Income account filter</strong>
          <span>
            {view.filtered
              ? `${formatArCurrency(view.totals.filteredOpenAmount)} selected of ${formatArCurrency(view.totals.allOpenAmount)}`
              : `${formatArCurrency(view.totals.allOpenAmount)} across all income accounts`}
          </span>
        </div>
        <button type="button" onClick={() => setExpanded((open) => !open)} aria-expanded={expanded}>
          {view.filtered ? `${view.selectedAccountIds.size.toLocaleString()} selected` : 'All accounts'}
        </button>
      </div>
      {expanded ? (
        <>
          <div className="cfo-ar-income-options is-inline" aria-label="Filter A/R by income account">
            {view.accountOptions.map((account) => {
              const checked = draftAllAccounts || draftSet.has(account.id)
              return (
                <label key={account.id} className={account.unclassified ? 'is-unclassified' : undefined}>
                  <input type="checkbox" checked={checked} onChange={() => toggleAccount(account)} />
                  <span>{account.name}</span>
                  <em>{formatArCurrency(account.openAmount)}</em>
                </label>
              )
            })}
          </div>
          <div className="cfo-ar-income-filter-actions">
            <button type="button" onClick={useAllAccounts}>Use all</button>
            <button type="button" onClick={cancelDraft}>Cancel</button>
            <button type="button" className="is-primary" onClick={applyDraft}>Apply</button>
          </div>
          {optionCount > 8 ? (
            <span className="cfo-ar-income-filter-more">{optionCount.toLocaleString()} income accounts available. Scroll the list to review all accounts.</span>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export function CfoArIncomeAccountSection({
  view,
  selectedAccountIds,
  onSelectedAccountIdsChange,
  metaLabel,
  exportAvailable,
  onExport,
}: {
  view: ArIncomeAccountReportView
  selectedAccountIds: string[]
  onSelectedAccountIdsChange: (ids: string[]) => void
  metaLabel: string
  exportAvailable: boolean
  onExport: CfoArExportTable
}) {
  const [sort, setSort] = useState<ArSortState<ArIncomeAccountAllocationSortColumn>>({ columnId: 'overNinetyAmount', direction: 'desc' })
  const visibleColumns = DEFAULT_INCOME_ACCOUNT_COLUMNS
  const sorted = useMemo(() => sortArIncomeAccountAllocations(view.rows, sort), [view.rows, sort])
  const visibleAccountCount = view.filtered ? view.selectedAccountIds.size : view.accountOptions.length
  const totals = useMemo(() => [
    { label: 'Rows', value: sorted.length.toLocaleString(), tone: 'muted' as const },
    { label: view.filtered ? 'Selected A/R' : 'Open A/R', value: formatArCurrency(view.totals.filteredOpenAmount) },
    { label: '90+', value: formatArCurrency(view.totals.overNinetyAmount), tone: 'risk' as const },
    { label: 'Unclassified', value: formatArCurrency(view.totals.unclassifiedAmount), tone: view.totals.unclassifiedAmount ? 'risk' as const : 'muted' as const },
  ], [sorted.length, view.filtered, view.totals])

  if (!view.allRows.length) {
    return <div className="cfo-shell-notice">No income-account allocation rows are available for this A/R period. FuseGrid will show this report after the A/R analysis artifact is rebuilt with synced invoice-line detail.</div>
  }

  return (
    <section className="cfo-ar-card">
      <CfoArSectionBar
        title="Customers & income accounts"
        meta={`${visibleAccountCount.toLocaleString()} income account${visibleAccountCount === 1 ? '' : 's'} · ${metaLabel}`}
        action={
          <CfoArDownloadButton
            label="A/R by income account"
            available={exportAvailable}
            disabled={!sorted.length}
            onClick={() =>
              onExport({
                title: 'A/R by customer and income account',
                sheetName: 'Income Accounts',
                fileSegment: 'AR Income Accounts',
                rows: sorted,
                columns: AR_INCOME_ACCOUNT_EXCEL_COLUMNS,
                meta: view.filtered
                  ? `${metaLabel} · selected income-account subset`
                  : `${metaLabel} · all income accounts`,
              })
            }
          />
        }
      />
      <CfoArTableTotals items={totals} />
      <CfoArIncomeAccountFilter
        view={view}
        selectedAccountIds={selectedAccountIds}
        onSelectedAccountIdsChange={onSelectedAccountIdsChange}
      />
      {view.filtered ? (
        <div className="cfo-shell-notice">
          Showing a selected income-account subset: {formatArCurrency(view.totals.filteredOpenAmount)} of {formatArCurrency(view.totals.allOpenAmount)} unfiltered open A/R.
        </div>
      ) : Math.abs(view.totals.reconciliationVariance) > 1 ? (
        <div className="cfo-shell-notice error">
          Unfiltered rows differ from selected-period A/R by {formatArCurrency(view.totals.reconciliationVariance)}. Check unclassified exception rows before using this cut.
        </div>
      ) : null}
      <div className="dtable-wrap cfo-ar-table-viewport">
        <table className="dtable">
          <thead>
            <tr>
              {visibleColumns.map((column) =>
                column.sort ? (
                  <SortHeader
                    key={column.id}
                    columnId={column.sort}
                    label={column.label}
                    sort={sort}
                    onSort={setSort}
                    align={column.align || 'right'}
                  />
                ) : (
                  <th key={column.id} style={{ textAlign: column.align || 'right' }}>{column.label}</th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.length ? (
              sorted.map((row, index) => (
                <tr key={`${row.periodEnd || ''}-${row.invoiceKey}-${row.invoiceLineId || row.incomeAccountId || index}`}>
                  {visibleColumns.map((column) => (
                    <td
                      key={`${row.invoiceKey}-${column.id}`}
                      style={{
                        textAlign: column.align || 'right',
                        fontWeight: column.id === 'customer' || column.id === 'incomeAccountName' ? 600 : undefined,
                        maxWidth: column.align === 'left' ? 240 : undefined,
                        overflow: column.align === 'left' ? 'hidden' : undefined,
                        textOverflow: column.align === 'left' ? 'ellipsis' : undefined,
                        whiteSpace: column.align === 'left' ? 'nowrap' : undefined,
                      }}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={Math.max(1, visibleColumns.length)} style={{ textAlign: 'left', color: 'var(--muted)' }}>
                  No rows match the selected income accounts.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ---- Collectability & Reserve ---------------------------------------------

function ConfidenceBadge({ confidence }: { confidence?: string | null }) {
  const tone = confidence === 'high' ? 'good' : confidence === 'medium' ? 'watch' : 'risk'
  return <span className={`cfo-ar-confidence cfo-ar-confidence-${tone}`}>{confidence || 'low'} confidence</span>
}

function ThresholdRecoveryCard({ threshold }: { threshold: QuickBooksArCollectabilityThreshold }) {
  const recovered = Math.max(0, Math.min(1, Number(threshold.amountRecoveryRate || 0)))
  const fullyPaid = Math.max(0, Math.min(1, Number(threshold.fullPaymentRate || 0)))
  return (
    <article className="cfo-ar-threshold-card">
      <div className="cfo-ar-threshold-head">
        <strong>{threshold.thresholdDays}+ days</strong>
        <ConfidenceBadge confidence={threshold.confidence} />
      </div>
      <div className="cfo-ar-meter">
        <span className="cfo-ar-meter-label">Dollars recovered</span>
        <span className="cfo-ar-meter-track"><span className="cfo-ar-meter-fill" style={{ width: `${recovered * 100}%`, background: 'var(--pos, #2f855a)' }} /></span>
        <span className="cfo-ar-meter-value">{formatArPercent(threshold.amountRecoveryRate)}</span>
      </div>
      <div className="cfo-ar-meter">
        <span className="cfo-ar-meter-label">Fully paid</span>
        <span className="cfo-ar-meter-track"><span className="cfo-ar-meter-fill" style={{ width: `${fullyPaid * 100}%`, background: 'var(--accent, #176b87)' }} /></span>
        <span className="cfo-ar-meter-value">{formatArPercent(threshold.fullPaymentRate)}</span>
      </div>
      <dl className="cfo-ar-threshold-grid">
        <div><dt>Any recovery</dt><dd>{formatArPercent(threshold.anyRecoveryRate)}</dd></div>
        <div><dt>Open at threshold</dt><dd>{formatArCurrency(threshold.openAtThresholdAmount)}</dd></div>
        <div><dt>Cohort invoices</dt><dd>{Number(threshold.cohortInvoiceCount || 0).toLocaleString()}</dd></div>
        <div><dt>Median full-pay lag</dt><dd>{formatArDays(threshold.medianDaysToFullPaymentAfterThreshold)}</dd></div>
      </dl>
    </article>
  )
}

const COLLECTABILITY_CUSTOMER_COLUMNS: ReadonlyArray<ArDataColumn<QuickBooksArCollectabilityCustomer, ArCollectabilityCustomerSortColumn>> = [
  { id: 'customer', label: 'Customer', sort: 'customer', align: 'left', render: (row) => row.customer, cellStyle: () => ({ fontWeight: 600 }) },
  { id: 'openAmount', label: 'Open', sort: 'openAmount', render: (row) => formatArCurrency(row.openAmount) },
  { id: 'over90Amount', label: '>90', sort: 'over90Amount', render: (row) => formatArCurrency(row.over90Amount) },
  { id: 'over180Amount', label: '>180', sort: 'over180Amount', render: (row) => formatArCurrency(row.over180Amount) },
  { id: 'over270Amount', label: '>270', sort: 'over270Amount', render: (row) => formatArCurrency(row.over270Amount) },
  { id: 'over360Amount', label: '>360', sort: 'over360Amount', render: (row) => formatArCurrency(row.over360Amount) },
  { id: 'expectedWriteOffAmount', label: 'Reserve', sort: 'expectedWriteOffAmount', render: (row) => formatArCurrency(row.expectedWriteOffAmount) },
  { id: 'worstPastDueDays', label: 'Worst days', sort: 'worstPastDueDays', render: (row) => formatArDayNumber(row.worstPastDueDays) },
]

const COLLECTABILITY_INVOICE_COLUMNS: ReadonlyArray<ArDataColumn<QuickBooksArCollectabilityInvoice, ArCollectabilityInvoiceSortColumn>> = [
  { id: 'invoiceNumber', label: 'Invoice', sort: 'invoiceNumber', align: 'left', render: (row) => row.invoiceNumber || row.invoiceKey, cellStyle: () => ({ fontWeight: 600 }) },
  {
    id: 'customer',
    label: 'Customer',
    sort: 'customer',
    align: 'left',
    render: (row) => row.customer || '',
    cellStyle: () => ({ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
  },
  { id: 'dueDate', label: 'Due', sort: 'dueDate', defaultDirection: 'asc', render: (row) => row.dueDate || '' },
  { id: 'openAmount', label: 'Open', sort: 'openAmount', render: (row) => formatArCurrency(row.openAmount) },
  { id: 'daysPastDue', label: 'Past due', sort: 'daysPastDue', render: (row) => formatArDayNumber(row.daysPastDue) },
  { id: 'thresholdDays', label: 'Cohort', sort: 'thresholdDays', render: (row) => (row.thresholdDays ? `${row.thresholdDays}+` : 'N/A') },
  { id: 'benchmarkRecoveryRate', label: 'Recovery', sort: 'benchmarkRecoveryRate', render: (row) => formatArPercent(row.benchmarkRecoveryRate) },
  { id: 'expectedWriteOffAmount', label: 'Reserve', sort: 'expectedWriteOffAmount', render: (row) => formatArCurrency(row.expectedWriteOffAmount) },
]

export function CfoArCollectabilitySection({
  collectability,
  view,
  exportAvailable,
  onExport,
}: {
  collectability: QuickBooksArCollectabilityAnalysis | null | undefined
  view: ArCollectabilityView
  exportAvailable: boolean
  onExport: CfoArExportTable
}) {
  const thresholds = collectability?.thresholds || []
  const reserve = collectability?.badDebtReserve || null
  const reserveMethods = useMemo(() => reserve?.methods || [], [reserve])
  const reserveTrend = useMemo(() => reserve?.trend || [], [reserve])
  const [reserveMethodId, setReserveMethodId] = useState<string>(reserve?.recommendedMethod || reserveMethods[0]?.id || 'aging_expected_loss')
  const [customerSort, setCustomerSort] = useState<ArSortState<ArCollectabilityCustomerSortColumn>>({ columnId: 'expectedWriteOffAmount', direction: 'desc' })
  const [invoiceSort, setInvoiceSort] = useState<ArSortState<ArCollectabilityInvoiceSortColumn>>({ columnId: 'expectedWriteOffAmount', direction: 'desc' })

  const selectedMethod = useMemo(
    () => reserveMethods.find((method) => method.id === reserveMethodId) || reserveMethods[0] || null,
    [reserveMethods, reserveMethodId],
  )
  const sortedCustomers = useMemo(
    () => sortArCollectabilityCustomers(view.customers.filter((customer) => Math.abs(Number(customer.openAmount || 0)) >= 1), customerSort),
    [view.customers, customerSort],
  )
  const sortedInvoices = useMemo(() => sortArCollectabilityInvoices(view.invoices, invoiceSort), [view.invoices, invoiceSort])
  const customerTotals = useMemo(() => [
    { label: 'Customers', value: sortedCustomers.length.toLocaleString(), tone: 'muted' as const },
    { label: 'Open at risk', value: formatArCurrency(sumBy(sortedCustomers, (customer) => customer.openAmount)) },
    { label: '90+', value: formatArCurrency(sumBy(sortedCustomers, (customer) => customer.over90Amount)), tone: 'risk' as const },
    { label: 'Reserve', value: formatArCurrency(sumBy(sortedCustomers, (customer) => customer.expectedWriteOffAmount)) },
  ], [sortedCustomers])
  const invoiceTotals = useMemo(() => [
    { label: 'Invoices', value: sortedInvoices.length.toLocaleString(), tone: 'muted' as const },
    { label: 'Open at risk', value: formatArCurrency(sumBy(sortedInvoices, (invoice) => invoice.openAmount)) },
    { label: 'Expected collectible', value: formatArCurrency(sumBy(sortedInvoices, (invoice) => invoice.expectedCollectibleAmount)), tone: 'good' as const },
    { label: 'Reserve', value: formatArCurrency(sumBy(sortedInvoices, (invoice) => invoice.expectedWriteOffAmount)) },
  ], [sortedInvoices])
  const exportMeta = `as of ${collectability?.asOfDate || reserve?.asOfDate || ''}`.trim()

  if (!collectability || !thresholds.length) {
    return (
      <div className="cfo-shell-notice">
        Collectability needs invoice-and-payment recovery history. FuseGrid is refreshing the A/R analysis artifact and will show these cohorts when they are available.
      </div>
    )
  }

  return (
    <div className="cfo-ar-collectability">
      <div className="cards">
        <article className="card">
          <div className="body"><div className="grow"><h3>{formatArCurrency(view.totalAtRiskOpen)}</h3><p>At-risk A/R · {view.invoices.length} invoice{view.invoices.length === 1 ? '' : 's'} 90+ days past due</p></div></div>
        </article>
        <article className="card">
          <div className="body"><div className="grow"><h3>{view.hasBenchmarks ? formatArCurrency(view.totalExpectedCollectible) : 'N/A'}</h3><p>Expected collectible · {formatArPercent(view.weightedRecoveryRate)} recovery</p></div></div>
        </article>
        <article className="card">
          <div className="body"><div className="grow"><h3>{view.hasBenchmarks ? formatArCurrency(view.totalExpectedWriteOff) : 'N/A'}</h3><p>Reserve signal · historical non-recovery after threshold</p></div></div>
        </article>
        <article className="card">
          <div className="body"><div className="grow"><h3>{formatArCurrency(view.over360Exposure)}</h3><p>360+ exposure · {formatArPercent(view.totalAtRiskOpen > 0 ? view.over360Exposure / view.totalAtRiskOpen : null)} of at-risk</p></div></div>
        </article>
      </div>

      {reserve && selectedMethod ? (
        <section className="cfo-ar-card cfo-ar-reserve">
          <CfoArSectionBar title="Bad-debt reserve" meta={exportMeta} />
          <div className="cfo-ar-reserve-grid">
            <div className="cfo-ar-reserve-methods">
              {reserveMethods.map((method) => (
                <button
                  type="button"
                  key={method.id}
                  className={`cfo-ar-reserve-method${method.id === selectedMethod.id ? ' is-selected' : ''}`}
                  onClick={() => setReserveMethodId(method.id)}
                >
                  <strong>{method.label}</strong>
                  <span>{formatArPercent(method.percentOfSales)} of sales</span>
                  {method.explanation ? <em>{method.explanation}</em> : null}
                </button>
              ))}
            </div>
            <div className="cfo-ar-reserve-detail">
              <div className="cfo-ar-reserve-tiles">
                <div><span>Target reserve</span><strong>{formatArCurrency(selectedMethod.reserveAmount)}</strong></div>
                <div><span>% of sales</span><strong>{formatArPercent(selectedMethod.percentOfSales)}</strong></div>
                <div><span>Month accrual</span><strong>{formatArCurrency(selectedMethod.currentMonthAccrual)}</strong></div>
              </div>
              {reserveTrend.length > 1 ? (
                <div className="cfo-ar-reserve-trend">
                  <span className="cfo-ar-reserve-trend-label">Rolling reserve % of sales</span>
                  <Sparkline
                    values={reserveTrend.map((point) => Number(point.percentOfSales || 0) * 100)}
                    ariaLabel="Rolling reserve percent of sales trend"
                    width={220}
                    height={40}
                  />
                  <span className="cfo-ar-reserve-trend-now">{formatArPercent(reserveTrend[reserveTrend.length - 1]?.percentOfSales)}</span>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      <section className="cfo-ar-card">
        <CfoArSectionBar title="Likelihood paid after aging threshold" />
        <div className="cfo-ar-threshold-cards">
          {thresholds.map((threshold) => (
            <ThresholdRecoveryCard key={`threshold-${threshold.thresholdDays}`} threshold={threshold} />
          ))}
        </div>
      </section>

      <section className="cfo-ar-card">
        <CfoArSectionBar
          title="Customer exposure"
          meta={`${sortedCustomers.length} customer${sortedCustomers.length === 1 ? '' : 's'}`}
          action={
            <CfoArDownloadButton
              label="Collectability customer exposure"
              available={exportAvailable}
              disabled={!sortedCustomers.length}
              onClick={() =>
                onExport({
                  title: 'Collectability customer exposure',
                  sheetName: 'Customer Exposure',
                  fileSegment: 'Collectability Customer Exposure',
                  rows: sortedCustomers,
                  columns: AR_COLLECTABILITY_CUSTOMER_EXCEL_COLUMNS,
                  meta: exportMeta,
                })
              }
            />
          }
        />
        <CfoArTableTotals items={customerTotals} />
        <div className="dtable-wrap cfo-ar-table-viewport">
          <table className="dtable">
            <thead>
              <tr>
                {COLLECTABILITY_CUSTOMER_COLUMNS.map((column) => <ArColumnHead key={column.id} column={column} sort={customerSort} onSort={setCustomerSort} />)}
              </tr>
            </thead>
            <tbody>
              {sortedCustomers.length ? (
                sortedCustomers.map((customer: QuickBooksArCollectabilityCustomer) => (
                  <tr key={`collect-customer-${arCustomerKey(customer) || customer.customer}`}>
                    <ArColumnCells columns={COLLECTABILITY_CUSTOMER_COLUMNS} row={customer} />
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={COLLECTABILITY_CUSTOMER_COLUMNS.length} style={{ textAlign: 'left', color: 'var(--muted)' }}>
                    No customers currently have invoices at least 90 days past due.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="cfo-ar-card">
        <CfoArSectionBar
          title="Invoices past threshold"
          meta={`${sortedInvoices.length} invoice${sortedInvoices.length === 1 ? '' : 's'}`}
          action={
            <CfoArDownloadButton
              label="Collectability invoice detail"
              available={exportAvailable}
              disabled={!sortedInvoices.length}
              onClick={() =>
                onExport({
                  title: 'Collectability invoices past threshold',
                  sheetName: 'Invoices',
                  fileSegment: 'Collectability Invoices',
                  rows: sortedInvoices,
                  columns: AR_COLLECTABILITY_INVOICE_EXCEL_COLUMNS,
                  meta: exportMeta,
                })
              }
            />
          }
        />
        <CfoArTableTotals items={invoiceTotals} />
        <div className="dtable-wrap cfo-ar-table-viewport">
          <table className="dtable">
            <thead>
              <tr>
                {COLLECTABILITY_INVOICE_COLUMNS.map((column) => <ArColumnHead key={column.id} column={column} sort={invoiceSort} onSort={setInvoiceSort} />)}
              </tr>
            </thead>
            <tbody>
              {sortedInvoices.length ? (
                sortedInvoices.map((invoice: QuickBooksArCollectabilityInvoice) => (
                  <tr key={`collect-invoice-${invoice.invoiceKey}`}>
                    <ArColumnCells columns={COLLECTABILITY_INVOICE_COLUMNS} row={invoice} />
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={COLLECTABILITY_INVOICE_COLUMNS.length} style={{ textAlign: 'left', color: 'var(--muted)' }}>
                    No open invoices currently cross the 90 day benchmark.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
